import {
    NODE_TYPES
} from "@modules/JourneyBuilder/Components/Charts/SankeyV2/constants";

const EMPTY_ARRAY = Object.freeze([]);

/**
 * Create a new filtered tree based on the given tree
 *
 * @param {JourneyBuilder.Sankey.Tree} tree
 * @param {function(branch: JourneyBuilder.Sankey.TreeBranchV2):boolean} isFiltered
 * @return {JourneyBuilder.Sankey.Tree}
 */
function filterTreeV2(tree, isFiltered) {
    /** @type {Map<String, boolean>} */
    const filterStatePerNode = new Map();

    /**  @type {JourneyBuilder.Sankey.TreeBranchV2[]} */
    const newRoots = [];

    /** @type {Map<String, JourneyBuilder.Sankey.TreeBranchV2>} */
    const newBranchLookup = new Map();

    let filteredTotalLevels = 0;

    /**
     * Cached wrapper around isFiltered
     * 
     * @param {JourneyBuilder.Sankey.TreeBranchV2} branch
     * @return {boolean}
     */
    const isFilteredImpl = branch => {
        if(!filterStatePerNode.has(branch.node.id)) {
            filterStatePerNode.set(branch.node.id, isFiltered(branch));
        }

        return filterStatePerNode.get(branch.node.id);
    }

    /**
     * For non filtered branches:
     * Create a copy, assign copy to target collection (parent children/root collection)
     * Re-calculate max total level
     * 
     * For filtered branches:
     * return NULL marker
     * 
     * @param {JourneyBuilder.Sankey.TreeBranchV2} branch
     * @param {JourneyBuilder.Sankey.TreeBranchV2|null} parent
     * @return {JourneyBuilder.Sankey.TreeBranchV2|null}
     */
    function processBranch(branch, parent) {
        if(isFilteredImpl(branch)){
            return null;
        }
        
        branch = {
            ...branch,
            node: {
                ...branch.node,
                from: parent ? parent.node.id : null
            },
            parent,
            children: [],
            level: parent ? parent.level + 1 : 0,
            containsFilteredChildren: branch.children.some(child => isFilteredImpl(child))
        }

        filteredTotalLevels = Math.max(filteredTotalLevels, branch.level);
        (parent && parent.children || newRoots).push(branch);
        newBranchLookup.set(branch.node.id, branch);
        
        return branch;
    }
    
    function walk(branch, parent) {
        //Even if the current branch gets filtered,  our children may still be included
        const children = branch.children;
        
        branch = processBranch(branch, parent);

        for(let i = 0; i < children.length; i++) {
            walk(children[i], branch || parent);
        }
    }

    for(let i = 0; i < tree.roots.length; i++){
        walk(tree.roots[i], null);
    }

    return {
        ...tree,
        getBranchByNodeId: nodeId => newBranchLookup.get(nodeId),
        roots: newRoots,
        totalLevels: filteredTotalLevels
    }
}

/**
 * @param {JourneyBuilder.Sankey.SankeyNode[]} data
 * @returns {JourneyBuilder.Sankey.Tree}
 */
function toTreeV2(data){
    var byId = {},
        byParentId = {},
        branchLookup = new Map(),
        goalBranches = [];
    
    data.forEach(item => {
        byId[item.id || ''] = item;
        let p = byParentId[item.from || ''] = byParentId[item.from || ''] || [];
        p.push(item);
    });
    
    // find disconnected items?
    // build a tree
    
    let totalLevels = 0;

    /**
     * 
     * @param {JourneyBuilder.Sankey.SankeyNode} node
     * @param {JourneyBuilder.Sankey.TreeBranchV2} parentBranch
     * @param {Number} level
     * @return {JourneyBuilder.Sankey.TreeBranchV2}
     */
    function walk(node, parentBranch, level = 0) {
        const children = byParentId[node.id] || EMPTY_ARRAY;
        
        const branch = {
            parent: parentBranch,
            node: node,
            children: children,
            level: level,
            containsFilteredChildren: false
        };
        
        //Goal nodes aren't included in the regular flow subtract a level and register them so we can move them to the end after we know our total levels
        if(node.isGoal) {
            level--;
            goalBranches.push(branch);
        }
        
        branchLookup.set(node.id, branch);
        totalLevels = Math.max(totalLevels, level);
        
        for(let j = 0; j < children.length; j++) {
            let child = children[j];
            children[j] = walk(child, branch, level + 1);
        }
        
        return branch;
    }

    const roots = byParentId[''] || [];
    
    for(let i = 0; i < roots.length; i++) {
        roots[i] = walk(roots[i], null)
    }

    //Goals all get rendered on the absolute final level
    if(goalBranches.length) {
        totalLevels++;
        goalBranches.forEach(goalBranch => goalBranch.level = totalLevels);
    }

    // move all audiences to the front, but maintain the order of the other elements
    roots.sort((left, right) => {
        if(left.node.type === right.node.type){
            return 0;
        }
        
        if(left.node.type === NODE_TYPES.AUDIENCE){
            return 1;
        }

        if(right.node.type === NODE_TYPES.AUDIENCE){
            return -1;
        }
        
        return 0;
    });

    return {
        roots,
        totalLevels,
        getBranchByNodeId: nodeId => branchLookup.get(nodeId),
        filter: function(isFiltered) { return filterTreeV2(this, isFiltered); },
        forAllBranches: function forAllBranches(callback){
            const forAllBranchesImpl = branches => {
                branches.forEach(branch => {
                    callback(branch);
                    forAllBranchesImpl(branch.children);
                });
            }

            forAllBranchesImpl(this.roots);
        }
    };
}

export {
    toTreeV2 as buildTree
}